import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation } from '@apollo/react-hooks';

import Button from '@welldev-group/rcl-button';
import Input from '@welldev-group/rcl-input';
import SelectBox from '@welldev-group/rcl-select-box';
import ErrorComponent from '../common/ErrorComponent';
import WithErrorHandler from '../common/WithErrorHandler';
import CheckBox from '../rclComponents/CheckBox';
import clearCache from '../../helpers/clearCache';
import PropTypes from 'prop-types';

import { CREATE_FAQ } from '../../graphql/mutations/faq';
import { GET_FAQ_CATEGORIES } from '../../graphql/queries/faq';
import { all } from '../../graphql/queries/prepareQueryParams';
import NoResultsFound from '../common/NoResultsFound';
import { debounce } from 'lodash';

const FaqLabelForm = props => {
  const { t } = useTranslation();

  const [createFaq, callbackDataOnCreate] = useMutation(CREATE_FAQ, {
    update: cache => clearCache(cache, /Faq/),
  });

  const { loading: faqListLoading, data: faqListData } = useQuery(GET_FAQ_CATEGORIES);

  const userId = props.userId;
  const [parentId, setParentId] = useState('');
  const [title, setTitle] = useState('');
  const [nestedLabel, setNestedLabel] = useState(false);

  const onFaqLabelIdChange = selectedOption => {
    setParentId(selectedOption.value);
  };

  const onFaqLabelChange = value => {
    setTitle(value);
  };

  const submitToBackEnd = debounce(() => {
    props.resetError();
    createFaq({
      variables: { title, parentId, userId },
      refetchQueries: [all(GET_FAQ_CATEGORIES, parentId)],
    })
      .then(() => {
        props.history.push('/admin/faqs');
      })
      .catch(() => props.onError());
  }, 500);

  let faqLabelOptions = [];
  if (!faqListLoading) {
    faqLabelOptions = faqListData.faqCategories.map(faq => ({
      value: faq.value,
      label: faq.label,
    }));
  }

  return (
    <div className="common-form">
      <div className="common-form__title">
        {t('shared.new', { entityName: t('faqs.attributes.label') })}
      </div>
      <div className="common-form__error-message">
        {props.hasError && (
          <div className="col-6">
            <ErrorComponent error={{ callbackDataOnCreate }} />
          </div>
        )}
      </div>
      <form className="common-form__container">
        <div className="grid">
          <div className="col-lg-8 col-md-10 col-sm-10 col-xs-10">
            <div className="col-lg-12 col-md-12 col-sm-12 col-xs-12 col-bleed-y">
              <div className="common-form__field faq-label-form__label">
                <Input
                    label={t('faqs.attributes.label')}
                    value={title}
                    onChange={onFaqLabelChange}
                    isClearable={false}
                />
              </div>
              <div className="col-grid col-bleed-x align-start common-form__field faq-label-form__faq-checkbox">
                <CheckBox
                  size="huge"
                  name="nestedLabel"
                  label={t('faqs.buttons.nested')}
                  checked={nestedLabel}
                  onChange={() => setNestedLabel(!nestedLabel)}
                />
              </div>
              {nestedLabel && (
                <div className="common-form__field faq-label-form__faq-label">
                  <SelectBox
                    width="large"
                    label={t('faqs.attributes.label')}
                    value={faqLabelOptions.find(option => option.value === parentId)}
                    options={faqLabelOptions}
                    onChange={onFaqLabelIdChange}
                    renderNoResultsFound={() => <NoResultsFound />}

                  />
                </div>
              )}
              <div className="common-form__submit-button">
                <Button label={t('shared.create')} onClick={submitToBackEnd} />
              </div>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
};

FaqLabelForm.propTypes = {
  userId: PropTypes.string.isRequired,
};

export default WithErrorHandler(FaqLabelForm);
